import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiCloseCircleOutline } from "@mdi/js";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  widthClass?: string;
  id?: string;
  compact?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  values,
  onChange,
  placeholder = "Select...",
  widthClass = "md:w-48",
  id,
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggleValue = (v: string) => {
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  };

  const label =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${values.length} selected`;

  return (
    <div className="flex items-center gap-2" id={id}>
      <div ref={rootRef} className={`relative w-full ${widthClass}`}>
        <button
          type="button"
          id={id ? `${id}-button` : undefined}
          onClick={() => setOpen((p) => !p)}
          className={`flex w-full items-center justify-between gap-2 rounded-md border bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-ring cursor-pointer text-left ${
            compact ? "h-7 px-2 text-xs" : "h-10 px-3 py-2 text-sm"
          }`}
        >
          <span
            className={`truncate ${values.length === 0 ? "text-gray-500" : ""}`}
          >
            {label}
          </span>
          <Icon
            path={mdiChevronDown}
            className={`pointer-events-none shrink-0 opacity-70 ${
              compact ? "h-4 w-4" : "h-5 w-5"
            }`}
          />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border bg-white shadow-lg">
            {options.map((opt) => {
              const checked = values.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  id={id ? `${id}-option-${opt.value}` : undefined}
                  className={`flex items-center gap-2 px-3 cursor-pointer hover:bg-gray-50 ${
                    compact ? "py-1.5 text-xs" : "py-2 text-sm"
                  }`}
                  style={{
                    display: "flex",
                    marginBottom: 0,
                    color: "inherit",
                    fontSize: compact ? "0.75rem" : undefined,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(opt.value)}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    style={{ height: "1rem", width: "1rem" }}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      {values.length > 0 && (
        <button
          onClick={() => onChange([])}
          className="text-red-500 hover:text-red-700 transition-colors"
          title="Clear selection"
        >
          <Icon path={mdiCloseCircleOutline} size={0.7} />
        </button>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
